import { emailCompositionDefaults, mergeEmailState } from '@kula-ai/components';
import type { EmailCompositionValue } from '@kula-ai/components';
import type { EmailCommConfigPayload } from '@/api/useJobStageActions/useCreateJobStageAction';
import type { InterviewEditorAttachmentItem } from '../types';
import { INTERVIEW_EMAIL_SENDER_ORGANIZER_VALUE } from '../types';

export const EMAIL_COMPOSITION_TO_MODE = 'single' as const;

/**
 * `emailCompositionToCommConfig` often emits objects with only `undefined` values; those are still
 * truthy and break `Object.keys` empty-checks. Treat as “no server data” so we keep editor defaults.
 */
/** True if the API/config blob has no meaningful subject, body, sender, or recipients. */
export function isVacuousEmailCommConfig(
  config: EmailCommConfigPayload | null | undefined
): boolean {
  if (config === null) {
    return true;
  }
  if (typeof config !== 'object' || Array.isArray(config)) {
    return true;
  }
  if (Object.keys(config as object).length === 0) {
    return true;
  }
  const c = config as Record<string, unknown>;
  const subject = c.subject;
  const body = c.body;
  const hasSubject = typeof subject === 'string' && subject.trim() !== '';
  const hasBody = typeof body === 'string' && body.trim() !== '';
  const hasFrom = c.from !== null && c.from !== '';
  const fromOrg = c.from_organizer === true;
  const cc = c.cc;
  const bcc = c.bcc;
  const hasCc = Array.isArray(cc) && cc.length > 0;
  const hasBcc = Array.isArray(bcc) && bcc.length > 0;
  const atts = c.attachments;
  const hasAttachments = Array.isArray(atts) && atts.length > 0;
  return !(
    hasSubject ||
    hasBody ||
    hasFrom ||
    fromOrg ||
    hasCc ||
    hasBcc ||
    hasAttachments
  );
}

function parseIdList(ids: string[] | undefined): number[] | undefined {
  if (!ids?.length) {
    return undefined;
  }
  const nums = ids.map((s) => Number(s)).filter((n) => !Number.isNaN(n));
  return nums.length ? nums : undefined;
}

/**
 * Maps a Kula `Email` composition snapshot into API `EmailCommConfigPayload`.
 */
/**
 * Merges editor attachment chips into an email comm config for multipart.
 * FormData expects `…[attachments][]` = raw `File` or attachment id (see shared email flows).
 */
export function attachEditorItemsToCommConfig(
  base: EmailCommConfigPayload,
  items: InterviewEditorAttachmentItem[]
): EmailCommConfigPayload {
  if (!items.length) {
    return base;
  }
  const attachments: Array<File | number> = [];
  for (const item of items) {
    if (item.file instanceof File) {
      attachments.push(item.file);
      continue;
    }
    const idNum = Number(item.key);
    if (item.key !== '' && !Number.isNaN(idNum) && String(idNum) === item.key) {
      attachments.push(idNum);
    }
  }
  if (!attachments.length) {
    return base;
  }
  return { ...base, attachments };
}

export function emailCompositionToCommConfig(
  value: EmailCompositionValue
): EmailCommConfigPayload {
  const rawFrom = value.from !== null ? String(value.from).trim() : '';
  if (rawFrom === INTERVIEW_EMAIL_SENDER_ORGANIZER_VALUE) {
    return {
      subject: value.subject?.trim() || undefined,
      body: value.content?.trim() ? value.content : undefined,
      from_organizer: true,
      cc: parseIdList(value.cc),
      bcc: parseIdList(value.bcc),
    };
  }
  const from = rawFrom !== '' ? Number(rawFrom) : undefined;
  return {
    subject: value.subject?.trim() || undefined,
    body: value.content?.trim() ? value.content : undefined,
    from: from !== undefined && !Number.isNaN(from) ? from : undefined,
    cc: parseIdList(value.cc),
    bcc: parseIdList(value.bcc),
  };
}

/**
 * Hydrate `EmailCompositionValue` from API / GET config.
 * GET may nest `sender: { id }` while POST uses `from` / `from_organizer` — map both.
 * When no sender is stored, default to organizer (matches PUT `from_organizer: true` when user picks Organizer).
 */
export function commConfigToEmailComposition(
  _config: EmailCommConfigPayload | null | undefined
): EmailCompositionValue {
  if (isVacuousEmailCommConfig(_config)) {
    return { ...emailCompositionDefaults };
  }
  const raw = _config as EmailCommConfigPayload & Record<string, unknown>;

  const senderRaw = raw.sender;
  let senderId: number | undefined;
  if (
    senderRaw &&
    typeof senderRaw === 'object' &&
    !Array.isArray(senderRaw) &&
    'id' in senderRaw
  ) {
    const n = Number((senderRaw as { id: unknown }).id);
    if (!Number.isNaN(n)) {
      senderId = n;
    }
  }

  let fromValue: string;
  if (raw.from_organizer === true) {
    fromValue = INTERVIEW_EMAIL_SENDER_ORGANIZER_VALUE;
  } else if (
    raw.from !== undefined &&
    raw.from !== null &&
    !Number.isNaN(Number(raw.from))
  ) {
    fromValue = String(raw.from);
  } else if (senderId !== undefined) {
    fromValue = String(senderId);
  } else {
    fromValue = INTERVIEW_EMAIL_SENDER_ORGANIZER_VALUE;
  }

  return mergeEmailState(
    { ...emailCompositionDefaults },
    {
      subject: raw.subject ?? '',
      content: raw.body ?? '',
      from: fromValue,
      cc: (raw.cc ?? []).map(String),
      bcc: (raw.bcc ?? []).map(String),
    },
    EMAIL_COMPOSITION_TO_MODE
  );
}

export function getDefaultEmailComposition(): EmailCompositionValue {
  return { ...emailCompositionDefaults };
}

export function patchEmailComposition(
  base: EmailCompositionValue,
  patch: Partial<EmailCompositionValue>
): EmailCompositionValue {
  return mergeEmailState(base, patch, EMAIL_COMPOSITION_TO_MODE);
}
